/**
 * mulberry32 — a tiny seeded PRNG (no dependency, deterministic across runs).
 *
 * Every generator is a pure function of a seed (PLAN.md §1): it draws from a
 * `mulberry32(seed)` stream, so `{subtest, difficulty, seed}` identifies a
 * question completely and regeneration always reproduces it exactly.
 *
 * Returns a draw function producing floats in [0, 1).
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return function next() {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Random integer in [min, max] (both inclusive). */
export function randomInt(draw: () => number, min: number, max: number): number {
  return min + Math.floor(draw() * (max - min + 1))
}

/** Uniform pick from a non-empty array. */
export function pick<T>(draw: () => number, items: readonly T[]): T {
  return items[Math.floor(draw() * items.length)]
}

/** In-place Fisher–Yates shuffle; returns the same array for chaining. */
export function shuffle<T>(draw: () => number, items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(draw() * (i + 1))
    ;[items[i], items[j]] = [items[j], items[i]]
  }
  return items
}
